import React, { useEffect, useMemo, useState } from "react";
import { useRfcLibrary } from "./RfcLibraryContext";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import FolderIcon from '@mui/icons-material/Folder';
import "./rfcstyles.css";
import { Breadcrumbs, TextField, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { ServiceApi } from "../../services/index.service";
import { Backdrop, CircularProgress } from "@mui/material";
import { Attachment as AttachmentIcon } from '@mui/icons-material';
import { PictureAsPdf as PdfIcon } from '@mui/icons-material';
import { InsertDriveFile as DocIcon } from '@mui/icons-material';
import { Description as WordIcon } from '@mui/icons-material';
import { TableChart as ExcelIcon } from '@mui/icons-material';
import ImageIcon from '@mui/icons-material/Image';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useLocation } from "react-router-dom";
import { oktaAuth, oktaLoginAuth } from "../../api/oidcApi";
import { GetLocalStore } from "../../utils/storage";


// Bucket from .env


const BUCKET = process.env.REACT_APP_RFC_BUCKET;

const getFileIcon = (fileName) => {
    const fileExtension = fileName.split('.').pop().toLowerCase();
    switch (fileExtension) {
        case 'pdf':
            return <PdfIcon style={{ marginRight: '8px', color: '#d41042' }} />;
        case 'doc':
        case 'docx':
            return <WordIcon style={{ marginRight: '8px' }} />;
        case 'xls':
        case 'xlsx':
            return <ExcelIcon style={{ marginRight: '8px' }} />;
        case 'jpg':
        case 'jpeg':
        case 'png':
            return <ImageIcon style={{ marginRight: '8px' }} />;
        default:
            return <AttachmentIcon style={{ marginRight: '8px' }} />;
    }
};

const displayFileName = (file) => {
    return file;
}

// ---- Client-side sorting helpers (Name / Last Modified) ----
const SORT_KEYS = { NAME: 'name', LAST_MODIFIED: 'last_modified' };

const nameCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

const toTime = (value) => {
    const t = value ? new Date(value).getTime() : NaN;
    return Number.isNaN(t) ? null : t;
};

// getName extracts the display name for an item (folder, file or search result)
const sortItems = (items, { key, direction }, getName) => {
    const dir = direction === 'asc' ? 1 : -1;
    return [...(items || [])].sort((a, b) => {
        let result = 0;
        if (key === SORT_KEYS.LAST_MODIFIED) {
            const ta = toTime(a?.last_modified);
            const tb = toTime(b?.last_modified);
            // Items without a date always go to the bottom
            if (ta === null && tb === null) result = 0;
            else if (ta === null) return 1;
            else if (tb === null) return -1;
            else result = ta - tb;
        } else {
            result = nameCollator.compare(getName(a) || '', getName(b) || '');
        }
        // Tie-breaker: name ascending keeps order stable
        if (result === 0) return nameCollator.compare(getName(a) || '', getName(b) || '');
        return result * dir;
    });
};

const getFolderName = (folder) => (folder?.folder_name ?? folder) || '';
const getFileName = (file) => file?.file_name || '';
const getSearchResultName = (file) => file?.path || '';
const RFCLibrary = () => {
    const serviceapi = new ServiceApi();
    const { setFileList } = useRfcLibrary();
    // Get base URL from .env
    const BASE_URL = process.env.REACT_APP_URL;
    // const navigate = useNavigate();
    // Get current store and store type from Redux
    const currentStore = useSelector((state) => state.storage?.store);
    const profile = useSelector((state) => state.profile);
    const isAdminOrSuperAdmin = ["Super Admin", "Admin"].includes(profile?.role);
    const [loading, setLoading] = useState(false);
    const [mainFolders, setMainFolders] = useState([]);
    const [subFolders, setSubFolders] = useState([]);
    const [files, setFiles] = useState([]);
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [shareModalOpen, setShareModalOpen] = useState(false);
    const [shareUrl, setShareUrl] = useState("");
    const [shareFileName, setShareFileName] = useState("");
    const [copied, setCopied] = useState(false);
    const [addFileModalOpen, setAddFileModalOpen] = useState(false);
    const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
    const [newFolderName, setNewFolderName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(false);
    const [uploadError, setUploadError] = useState("");
    const [uploadSuccess, setUploadSuccess] = useState(false);
    const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
    const FILE_SIZE_ERROR = "Max file size exceeded. Max file size to upload is 10 MB.";
    // Default matches previous behaviour: newest first
    const [sortConfig, setSortConfig] = useState({ key: SORT_KEYS.LAST_MODIFIED, direction: 'desc' });
    const location = useLocation();

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            // Name starts A→Z, Last Modified starts newest first
            return { key, direction: key === SORT_KEYS.NAME ? 'asc' : 'desc' };
        });
    };

    const sortedSubFolders = useMemo(() => sortItems(subFolders, sortConfig, getFolderName), [subFolders, sortConfig]);
    const sortedFilesView = useMemo(() => sortItems(files, sortConfig, getFileName), [files, sortConfig]);
    const sortedSearchResultsView = useMemo(() => sortItems(searchResults, sortConfig, getSearchResultName), [searchResults, sortConfig]);

    const renderSortHeader = (label, key) => {
        const active = sortConfig.key === key;
        const Icon = active && sortConfig.direction === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon;
        return (
            <span
                role="button"
                tabIndex={0}
                title={`Sort by ${label}`}
                aria-sort={active ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                onClick={() => handleSort(key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSort(key); } }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', cursor: 'pointer', userSelect: 'none' }}
            >
                {label}
                <Icon fontSize="small" style={{ opacity: active ? 1 : 0.3 }} />
            </span>
        );
    };
    const filePath = location.search.split('?file=').pop().split('&')[0];

    // Fetch Okta apps for the current user on every mount so that
    // enabled_features always reflects the latest Okta assignments.
    useEffect(() => {
        if (!profile?.sub) return;
        const featuresKey = `enabled_features_${profile.sub}`;
        serviceapi.getApps(profile.sub)
            .then((res) => {
                // API returns a plain array directly in res.data
                const apps = Array.isArray(res?.data) ? res.data : (res?.data?.body || []);
                const labels = apps.map(a => a.label?.toLowerCase());
                // enabled_features: based on "hand-scooped library" label
                const hasHandScooped = labels.includes("hand-scooped library - dst");
                const enabledFeatures = hasHandScooped ? ["hand_scooped"] : [];
                localStorage.setItem(featuresKey, JSON.stringify(enabledFeatures));
                fetchDataFromS3("");
            })
            .catch(() => {
                localStorage.setItem(featuresKey, JSON.stringify([]));
                fetchDataFromS3("");
            });
    }, [profile?.sub]);

    useEffect(() => {
        // If route contains filePath param, handle redirect after auth
        const params = new URLSearchParams(location.search);
        console.log("RFCLibrary: Checking for filePath in URL params:", params.toString());
        // const filePath = params.get('file');
        console.log("RFCLibrary: Extracted filePath from URL:", filePath);
        // if (filePath) {
        //     // Fetch file info and open after auth
        //     // You may want to fetch presigned_url here if needed
        //     // For now, just show a message or handle logic
        // }
        // Reset navigation state when store changes, then fetch root level
        setBreadcrumb([]);
        setSubFolders([]);
        setFiles([]);
        setSearchQuery("");
        setSearchResults([]);
        fetchDataFromS3("");
    }, [currentStore?.intStoreID]);

    const fetchDataFromS3 = async (prefix) => {
        setLoading(true);
        setFiles([]);
        setSubFolders([]);
        try {
            let enabled_features = [];
            if (profile?.sub) {
                const featuresKey = `enabled_features_${profile.sub}`;
                try {
                    enabled_features = JSON.parse(localStorage.getItem(featuresKey) || "[]");
                } catch (e) {
                    enabled_features = [];
                }
            }
            const req = {
                "prefix": `${prefix}`,
                "enabled_features": enabled_features
            }
            console.log('RFCLibrary: Fetching S3 data with url:', req);
            console.log('RFCLibrary: Fetching S3 data from prefix:', req.prefix);
            const { folders, files } = await serviceapi.getRfcLibraryDataFromS3(req);
            const sortedFiles = [...files].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
            const sortedFolders = [...folders].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
            setFiles(sortedFiles);
            // Always transform files for context and localStorage
            const transformedFiles = sortedFiles.map(file => ({
                ...file,
                path: file.file_name,
                open_url: file.presigned_url
            }));
            setFileList(transformedFiles);
            try {
                console.log('RFCLibrary: Saving file list to localStorage:', transformedFiles);
                localStorage.setItem('rfc_file_list', JSON.stringify(transformedFiles));
                console.log('RFCLibrary: Saved file list to localStorage:', transformedFiles);
            } catch (e) {
                console.error('Error saving file list to localStorage:', e);
            }
            setLoading(false);
            if (prefix === "") {
                const PINNED = ["Recent", "Hand-Scooped Locations"];
                const pinnedFolders = PINNED
                    .map(name => sortedFolders.find(f => (f.folder_name ?? f) === name))
                    .filter(Boolean);
                const restFolders = sortedFolders.filter(f => !PINNED.includes(f.folder_name ?? f));
                setMainFolders([...pinnedFolders, ...restFolders]);
            } else {
                setSubFolders(sortedFolders);
            }
        } catch (error) {
            console.error('Error fetching S3 objects:', error);
        }
        finally {
            setLoading(false);
        }
    };

    // Search API integration
    const handleSearch = async () => {

        // Check if authenticated
        let idToken = null;
        try {
            const tokenObj = await oktaAuth.tokenManager.get("idToken");
            idToken = tokenObj?.idToken || tokenObj?.value || tokenObj;
        } catch (e) {
            // idToken = GetLocalStorage("token");
        }


        if (!idToken) {
            // RequireAuth already saved the path before redirecting here
            // Just trigger login - no need to save anything
            await oktaLoginAuth();
            setLoading(false);
            return;

        }

        if (!searchQuery) return;
        setLoading(true);
        setBreadcrumb([]);
        setSubFolders([]);
        setFiles([]);
        try {
            let enabled_features = [];
            if (profile?.sub) {
                const featuresKey = `enabled_features_${profile.sub}`;
                try {
                    enabled_features = JSON.parse(localStorage.getItem(featuresKey) || "[]");
                } catch (e) {
                    enabled_features = [];
                }
            }
            const payload = {
                query: searchQuery,
                prefix: "",
                "enabled_features": enabled_features
            };

            const data = await serviceapi.getRfcLibraryDataFromS3(payload);
            const sortedSearchResults = [...(data.files || [])].sort((a, b) => new Date(b.last_modified) - new Date(a.last_modified));
            setSearchResults(sortedSearchResults);
        } catch (error) {
            console.error("Error searching RFC Library:", error);
        }
        setLoading(false);
    };

    const handleClearSearch = () => {
        setSearchQuery("");
        setSearchResults([]);
        setBreadcrumb([]);
        setSubFolders([]);
        setFiles([]);
        setMainFolders([]);
        fetchDataFromS3("");
    };

    // Handle folder click (update current path)
    const handleFolderClick = (folder) => {
        const name = folder.folder_name ?? folder;
        fetchDataFromS3(name);
        setBreadcrumb([name]);
    };

    const handleSubFolderClick = (folder) => {
        setLoading(true);
        const name = folder.folder_name ?? folder;
        setBreadcrumb((prevItems) => [...prevItems, name]);
    }
    useEffect(() => {
        if (breadcrumb.length > 0) {
            const path = breadcrumb.join("/");
            fetchDataFromS3(path);
        }
    }, [breadcrumb]);


    // Handle breadcrumb click (go back in folder structure)
    const handleBreadcrumbClick = (index, crumb) => {
        setBreadcrumb((prevItems) => prevItems.slice(0, index + 1));
    };

    const downloadFile = (file) => {
        const url = file.download_url || file.presigned_url;
        if (!url) {
            console.error('No download URL available for file:', file);
            return;
        }
        window.open(url, "_blank");
    }

    // Builds the app's own view route for a file, instead of exposing the raw S3 url
    const buildViewUrl = (file) => {
        const path = file.path || file.file_name;
        if (!path) return null;
        const encodedPath = path.split('/').map(encodeURIComponent).join('/');
        return `${BASE_URL}/rfc-library/${encodedPath}`;
    };

    const previewFile = (file) => {
        const url = buildViewUrl(file);
        if (url) {
            window.open(url, "_blank");
        } else {
            alert("File not found or cannot be opened. Check the file path and API response.");
        }
    };

    const handleShare = (file, isSearchResult = false) => {
        const path = file.path || file.file_name;
        if (!path) return;
        const url = buildViewUrl(file);
        setShareUrl(url);
        setShareFileName(path);
        setShareModalOpen(true);
    };

    const handleCopyShareLink = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCloseShareModal = () => {
        setShareModalOpen(false);
        setShareUrl("");
        setShareFileName("");
    };

    // Current folder prefix derived from breadcrumb
    const currentPrefix = breadcrumb.length > 0 ? breadcrumb.join("/") + "/" : "";

    const handleFileUpload = async () => {
        if (!selectedFile) return;
        if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
            setUploadError(FILE_SIZE_ERROR);
            return;
        }

        setUploadProgress(true);
        setUploadError("");
        try {
            // Get a pre-signed S3 POST, then upload directly to S3 (avoids the 6 MB Lambda payload limit)
            const res = await serviceapi.uploadToRfcLibrary({
                type: "upload_url",







                file_name: selectedFile.name,
                file_size: selectedFile.size,
                content_type: selectedFile.type || "application/octet-stream",
                prefix: currentPrefix,
            });
            if (!res || res.success === false || !res.upload_url) {
                setUploadError(res?.message || "Upload failed. Please try again.");
                setUploadProgress(false);
                return;
            }
            const formData = new FormData();
            Object.entries(res.fields).forEach(([key, value]) => formData.append(key, value));
            formData.append("file", selectedFile); // must be the last field
            // fetch, not axios: the axios interceptor adds an Authorization header that S3 rejects
            const s3Response = await fetch(res.upload_url, { method: "POST", body: formData });
            if (!s3Response.ok) {
                const s3Error = await s3Response.text();
                setUploadError(s3Error.includes("EntityTooLarge") ? FILE_SIZE_ERROR : "Upload failed. Please try again.");
                setUploadProgress(false);
                return;
            }
            setUploadSuccess(true);
            fetchDataFromS3(breadcrumb.join("/"));
            setTimeout(() => {
                setAddFileModalOpen(false);
                setSelectedFile(null);
                setUploadSuccess(false);
            }, 2000);
        } catch (error) {
            console.error("Error uploading file:", error);
            // postData rejects with a string like "Request failed with status code 413"
            setUploadError(String(error).includes("413") ? FILE_SIZE_ERROR : "Upload failed. Please try again.");
        }
        setUploadProgress(false);
    };

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;
        setUploadProgress(true);
        setUploadError("");
        try {
            const res = await serviceapi.uploadToRfcLibrary({
                type: "folder",
                prefix: currentPrefix,
                folder_name: newFolderName.trim()
            });
            if (res && res.success === false) {
                setUploadError(res.message || "Could not create folder. Please try again.");
                setUploadProgress(false);
                return;
            }
            setAddFolderModalOpen(false);
            setNewFolderName("");
            fetchDataFromS3(breadcrumb.join("/"));
        } catch (error) {
            console.error("Error creating folder:", error);
            setUploadError(error?.message || "Could not create folder. Please try again.");
        }
        setUploadProgress(false);
    };

    const handleDeleteFile = async (file) => {
        const fileName = file.file_name || file.path || "";
        if (!window.confirm(`Are you sure you want to delete "${fileName}"?`)) return;
        try {
            const s3Key = file.s3_key || (currentPrefix + fileName);
            await serviceapi.uploadToRfcLibrary({ type: "delete", s3_key: s3Key });
            fetchDataFromS3(breadcrumb.join("/"));
        } catch (error) {
            console.error("Error deleting file:", error);
            alert("Failed to delete file. Please try again.");
        }
    };

    const handleDeleteFolder = async (folder) => {
        const folderName = folder.folder_name ?? folder;
        if (!window.confirm(`Are you sure you want to delete the folder "${folderName}" and all its contents?`)) return;
        try {
            const s3Prefix = currentPrefix + folderName + "/";
            await serviceapi.uploadToRfcLibrary({ type: "delete_folder", s3_prefix: s3Prefix });
            fetchDataFromS3(breadcrumb.join("/"));
        } catch (error) {
            console.error("Error deleting folder:", error);
            alert("Failed to delete folder. Please try again.");
        }
    };

    return (
        <>
            <main>
                <Backdrop
                    sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                    open={loading}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 mt-16">
                    <h1 className="text-2xl font-display font-medium leading-tight text-secondary mb-4">
                        RFC Library <div className="breadcrumb"></div>
                    </h1>
                    {/* Search Bar styled like provided image */}
                    <div className="search-bar-row" style={{ marginBottom: "20px", display: "flex", alignItems: "center", justifyContent: "flex-start", gap: "8px", flexWrap: "wrap" }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            background: "#f3f3f3",
                            borderRadius: "8px",
                            padding: "0px 16px",
                            width: "100%",
                            maxWidth: "320px",
                            height: "44px",
                            position: "relative"
                        }}>
                            <SearchIcon style={{ color: "#a0a0a0", marginRight: "8px", fontSize: "22px" }} />
                            <input
                                type="text"
                                placeholder="search for files"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
                                style={{
                                    border: "none",
                                    outline: "none",
                                    background: "transparent",
                                    fontSize: "16px",
                                    color: "#333",
                                    width: "100%",
                                    height: "40px"
                                }}
                            />
                            {searchQuery && (
                                <ClearIcon
                                    onClick={handleClearSearch}
                                    style={{
                                        position: "absolute",
                                        right: "8px",
                                        cursor: "pointer",
                                        color: "#a0a0a0",
                                        fontSize: "22px"
                                    }}
                                    titleAccess="Clear search"
                                />
                            )}
                        </div>
                        <button
                            onClick={handleSearch}
                            style={{
                                background: "#d41042",
                                color: "#fff",
                                border: "none",
                                borderRadius: "8px",
                                padding: "0px 28px",
                                height: "44px",
                                fontSize: "16px",
                                fontWeight: "500",
                                cursor: "pointer",
                                boxShadow: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            Search
                        </button>
                    </div>
                    <div className={`container ${breadcrumb.length > 0 || searchResults.length > 0 ? 'mobile-show-files' : 'mobile-show-folders'}`}>
                        {/* Hide left panel folders if searchResults are present */}
                        {searchResults.length === 0 && (
                            <div className="left-panel">
                                <ul className="left-panel-ul">
                                    {mainFolders.map((folder) => {
                                        const name = folder.folder_name ?? folder;
                                        const lm = folder.last_modified;
                                        return (
                                            <li className="left-panel-li" key={name} onClick={() => handleFolderClick(folder)}>
                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                    <FolderIcon style={{ marginRight: '8px', color: '#f5b800', flexShrink: 0 }} />
                                                    <div>
                                                        <div>{name}</div>
                                                        {lm && (
                                                            <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                                                {new Date(lm).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}

                        <div className="right-panel" style={{ width: searchResults.length > 0 ? '100%' : undefined }}>
                            <div className="rfc-toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', minHeight: '36px' }}>
                                {breadcrumb.length > 0 ? (
                                    <Breadcrumbs aria-label="breadcrumb" style={{ fontSize: '14px' }}>
                                        <span
                                            style={{ cursor: 'pointer', color: '#d41042', fontWeight: 500 }}
                                            onClick={() => { setBreadcrumb([]); setSubFolders([]); setFiles([]); fetchDataFromS3(""); }}
                                        >
                                            Home
                                        </span>
                                        {breadcrumb.map((crumb, index) => {
                                            const isLast = index === breadcrumb.length - 1;
                                            const label = crumb.replace(/\/+$/, '');
                                            return isLast ? (
                                                <span key={index} style={{ color: '#333', fontWeight: 500 }}>{label}</span>
                                            ) : (
                                                <span
                                                    key={index}
                                                    style={{ cursor: 'pointer', color: '#d41042', fontWeight: 500 }}
                                                    onClick={() => handleBreadcrumbClick(index, crumb)}
                                                >
                                                    {label}
                                                </span>
                                            );
                                        })}
                                    </Breadcrumbs>
                                ) : <div />}
                                <div className="rfc-toolbar-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {isAdminOrSuperAdmin && <button
                                        onClick={() => { setSelectedFile(null); setUploadError(""); setAddFileModalOpen(true); }}
                                        disabled={breadcrumb[0] === "Recent" || searchResults.length > 0}
                                        style={{
                                            background: "#fff",
                                            color: breadcrumb[0] === "Recent" || searchResults.length > 0 ? "#ccc" : "#d41042",
                                            border: `2px solid ${breadcrumb[0] === "Recent" || searchResults.length > 0 ? "#ccc" : "#d41042"}`,
                                            borderRadius: "8px",
                                            padding: "0px 14px",
                                            height: "34px",
                                            fontSize: "13px",
                                            fontWeight: "500",
                                            cursor: breadcrumb[0] === "Recent" || searchResults.length > 0 ? "not-allowed" : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span> Add File
                                    </button>}
                                    {isAdminOrSuperAdmin && <button
                                        onClick={() => { setNewFolderName(""); setUploadError(""); setAddFolderModalOpen(true); }}
                                        disabled={breadcrumb[0] === "Recent" || searchResults.length > 0}
                                        style={{
                                            background: "#fff",
                                            color: breadcrumb[0] === "Recent" || searchResults.length > 0 ? "#ccc" : "#d41042",
                                            border: `2px solid ${breadcrumb[0] === "Recent" || searchResults.length > 0 ? "#ccc" : "#d41042"}`,
                                            borderRadius: "8px",
                                            padding: "0px 14px",
                                            height: "34px",
                                            fontSize: "13px",
                                            fontWeight: "500",
                                            cursor: breadcrumb[0] === "Recent" || searchResults.length > 0 ? "not-allowed" : "pointer",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "4px",
                                            whiteSpace: "nowrap"
                                        }}
                                    >
                                        <span style={{ fontSize: "18px", lineHeight: 1 }}>+</span> Add Folder
                                    </button>}
                                </div>
                            </div>
                            <div style={{ width: '100%', background: '#fff', maxWidth: '100%', borderRadius: '8px', boxShadow: 'none', paddingBottom: '8px', overflow: 'hidden' }}>
                                <div className="rfc-table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, fontSize: '18px', color: '#d41042', borderBottom: '3px solid #d41042', padding: '8px 0', margin: '0 0 8px 0', width: '100%' }}>
                                    <div style={{ flex: 2, textAlign: 'left', paddingLeft: '32px', minWidth: 0, overflow: 'hidden' }}>{renderSortHeader('Name', SORT_KEYS.NAME)}</div>
                                    <div className="rfc-table-header-lastmod" style={{ flex: '0 0 190px', textAlign: 'left', paddingLeft: '8px' }}>{renderSortHeader('Last Modified', SORT_KEYS.LAST_MODIFIED)}</div>
                                    <div className="rfc-table-header-spacer" style={{ flex: '0 0 65px', textAlign: 'center' }}></div>
                                    <div className="rfc-table-header-spacer" style={{ flex: '0 0 65px', textAlign: 'center' }}></div>
                                    <div className="rfc-table-header-spacer" style={{ flex: '0 0 65px', textAlign: 'center' }}></div>
                                    {isAdminOrSuperAdmin && <div className="rfc-table-header-spacer" style={{ flex: '0 0 65px', textAlign: 'center' }}></div>}
                                </div>
                                {/* SubFolders */}
                                {searchResults.length === 0 && sortedSubFolders.map((folder, index) => {
                                    const name = folder.folder_name ?? folder;
                                    const lm = folder.last_modified;
                                    return (
                                        <div className="file-row" key={name} style={{ display: 'flex', alignItems: 'center', background: index % 2 === 0 ? '#fff' : '#f8f6fa', minHeight: '48px', borderBottom: '1px solid #e2e2e2', cursor: 'pointer', width: '100%' }} onClick={() => handleSubFolderClick(folder)}>
                                            <div style={{ flex: 2, display: 'flex', alignItems: 'center', paddingLeft: '32px', fontWeight: 500, minWidth: 0, overflow: 'hidden' }}>
                                                <FolderIcon style={{ marginRight: '8px', color: '#f5b800', flexShrink: 0 }} />
                                                <span>{name}</span>
                                            </div>
                                            <div style={{ flex: '0 0 190px', paddingLeft: '8px', fontSize: '13px', color: '#d41042', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                                                {lm ? new Date(lm).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                            </div>
                                            <div style={{ flex: '0 0 65px' }}></div>
                                            <div style={{ flex: '0 0 65px' }}></div>
                                            <div style={{ flex: '0 0 65px' }}></div>
                                            {isAdminOrSuperAdmin && <div style={{ flex: '0 0 65px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                                                <IconButton
                                                    onClick={() => handleDeleteFolder(folder)}
                                                    size="small"
                                                    style={{ color: '#c0392b', padding: 2, width: 28, height: 28 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </div>}
                                        </div>
                                    );
                                })}

                                {/* Files */}
                                {searchResults.length === 0 && sortedFilesView.map((file, index) => (
                                    file.file_name ? (
                                        <div className="file-row" key={file.file_name} style={{ display: 'flex', alignItems: 'center', background: index % 2 === 0 ? '#fff' : '#f8f6fa', minHeight: '48px', borderBottom: '1px solid #e2e2e2', width: '100%' }}>
                                            <div style={{ flex: 2, display: 'flex', alignItems: 'center', paddingLeft: '32px', fontWeight: 500, minWidth: 0, overflow: 'hidden' }}>
                                                {getFileIcon(file.file_name)}
                                                <a
                                                    className="file-name-link"
                                                    title={file.file_name}
                                                    onClick={() => previewFile(file)}
                                                    style={{
                                                        marginLeft: 12,
                                                        color: '#1976d2',
                                                        fontWeight: 500,
                                                        fontSize: '16px',
                                                        textDecoration: 'underline',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'normal',
                                                        display: 'inline',
                                                        verticalAlign: 'middle'
                                                    }}
                                                >{file.file_name}</a>
                                            </div>
                                            <div style={{ flex: '0 0 190px', paddingLeft: '8px', fontSize: '13px', color: '#d41042', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                                                {file.last_modified ? new Date(file.last_modified).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                            </div>
                                            <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                                <IconButton
                                                    onClick={() => previewFile(file)}
                                                    size="small"
                                                    style={{ color: '#d41042', padding: 2, width: 28, height: 28 }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </div>

                                            <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                                <IconButton
                                                    onClick={() => downloadFile(file)}
                                                    size="small"
                                                    style={{ color: '#d41042', padding: 2, width: 28, height: 28 }}
                                                >
                                                    <FileDownloadIcon fontSize="small" />
                                                </IconButton>
                                            </div>

                                            <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                                <IconButton
                                                    onClick={() => handleShare(file, false)}
                                                    size="small"
                                                    style={{ color: '#d41042', padding: 2, width: 28, height: 28 }}
                                                >
                                                    <ShareIcon fontSize="small" />
                                                </IconButton>
                                            </div>
                                            {isAdminOrSuperAdmin && <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                                <IconButton
                                                    onClick={() => handleDeleteFile(file)}
                                                    size="small"
                                                    style={{ color: '#c0392b', padding: 2, width: 28, height: 28 }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </div>}
                                        </div>
                                    ) : null
                                ))}

                                {/* Search Results: show only path, not file_name */}
                                {searchResults.length > 0 && sortedSearchResultsView.map((file, index) => (
                                    <div className="file-row" key={file.path} style={{ display: 'flex', alignItems: 'center', background: index % 2 === 0 ? '#fff' : '#f8f6fa', minHeight: '48px', borderBottom: '1px solid #e2e2e2', width: '100%' }}>
                                        <div style={{ flex: 2, display: 'flex', alignItems: 'center', paddingLeft: '32px', fontWeight: 500, minWidth: 0, overflow: 'hidden' }}>
                                            {getFileIcon(file.path)}
                                            <a
                                                className="file-name-link"
                                                title={file.path}
                                                onClick={() => previewFile(file)}
                                                style={{
                                                    marginLeft: 12,
                                                    color: '#1976d2',
                                                    fontWeight: 500,
                                                    fontSize: '16px',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    whiteSpace: 'normal',
                                                    wordBreak: 'break-all',
                                                    display: 'inline',
                                                    verticalAlign: 'middle'
                                                }}
                                            >{file.path}</a>
                                        </div>
                                        <div style={{ flex: '0 0 190px', paddingLeft: '8px', fontSize: '13px', color: '#d41042', fontWeight: 700, display: 'flex', alignItems: 'center' }}>
                                            {file.last_modified ? new Date(file.last_modified).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                        </div>
                                        <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                            <IconButton onClick={() => previewFile(file)} size="small" style={{ color: '#d41042', padding: 2, width: 28, height: 28 }}>
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                        <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                            <IconButton onClick={() => window.open(file.download_url, "_blank")} size="small" style={{ color: '#d41042', padding: 2, width: 28, height: 28 }}>
                                                <FileDownloadIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                        <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                            <IconButton onClick={() => handleShare(file, true)} size="small" style={{ color: '#d41042', padding: 2, width: 28, height: 28 }}>
                                                <ShareIcon fontSize="small" />
                                            </IconButton>
                                        </div>
                                        {isAdminOrSuperAdmin && <div style={{ flex: '0 0 65px', textAlign: 'center' }}>
                                            <IconButton onClick={() => handleDeleteFile(file)} size="small" style={{ color: '#c0392b', padding: 2, width: 28, height: 28 }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </div>}
                                    </div>
                                ))}

                                {/* No content found */}
                                {searchResults.length === 0 && subFolders.length === 0 && files.length === 0 && (
                                    <div style={{ textAlign: 'center', padding: '20px', height: '100%' }}>
                                        <div className="no-content">No content found</div>
                                    </div>
                                )}
                            </div>
                        </div>


                    </div>
                </div>
            </main>

            {/* Share Modal */}
            {shareModalOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                    onClick={handleCloseShareModal}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="share-modal-title"
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '32px',
                            maxWidth: '500px',
                            width: '90%',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                            position: 'relative'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Close Button */}
                        <IconButton
                            onClick={handleCloseShareModal}
                            style={{
                                position: 'absolute',
                                top: '12px',
                                right: '12px',
                                color: '#666'
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {/* Title */}
                        <h2 id="share-modal-title" style={{
                            marginTop: 0,
                            marginBottom: '12px',
                            color: '#333',
                            fontSize: '20px',
                            fontWeight: 600
                        }}>
                            Share File
                        </h2>

                        {/* File Name */}
                        <p style={{
                            margin: '8px 0 16px 0',
                            color: '#666',
                            fontSize: '14px'
                        }}>
                            <strong>File:</strong> {shareFileName}
                        </p>

                        {/* Share Link Container */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '24px'
                        }}>
                            <div style={{
                                flex: 1,
                                backgroundColor: '#f5f5f5',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                border: '1px solid #e0e0e0',
                                fontSize: '14px',
                                color: '#333',
                                wordBreak: 'break-all',
                                maxHeight: '80px',
                                overflowY: 'auto',
                                fontFamily: 'monospace'
                            }}>
                                {shareUrl}
                            </div>
                            <IconButton
                                onClick={handleCopyShareLink}
                                style={{
                                    color: copied ? '#2e7d32' : '#d41042',
                                    padding: '8px',
                                    backgroundColor: copied ? '#e8f5e9' : '#f5f5f5',
                                    borderRadius: '8px',
                                    transition: 'color 0.2s, background-color 0.2s'
                                }}
                                title={copied ? 'Copied!' : 'Copy to clipboard'}
                            >
                                {copied ? <CheckIcon /> : <ContentCopyIcon />}
                            </IconButton>
                        </div>

                        {/* Close Button */}
                        <button
                            onClick={handleCloseShareModal}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '12px 16px',
                                backgroundColor: '#d41042',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontWeight: 500,
                                cursor: 'pointer'
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Add File Modal */}
            {addFileModalOpen && (
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 9999
                    }}
                    onClick={() => { setAddFileModalOpen(false); setUploadError(""); }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="add-file-modal-title"
                        style={{
                            backgroundColor: 'white', borderRadius: '12px', padding: '32px',
                            maxWidth: '480px', width: '90%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            position: 'relative'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <IconButton onClick={() => { setAddFileModalOpen(false); setUploadError(""); setUploadSuccess(false); }}
                            style={{ position: 'absolute', top: '12px', right: '12px', color: '#666' }}>
                            <CloseIcon />
                        </IconButton>
                        <h2 id="add-file-modal-title" style={{ marginTop: 0, marginBottom: '8px', color: '#333', fontSize: '20px', fontWeight: 600 }}>
                            Add File
                        </h2>
                        <p style={{ margin: '0 0 16px 0', color: '#888', fontSize: '13px' }}>
                            Uploading to: <strong style={{ color: '#d41042' }}>{currentPrefix || 'Root'}</strong>
                        </p>

                        {/* Success banner */}
                        {uploadSuccess && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#1a7f3c', color: 'white', borderRadius: '8px', padding: '14px 18px', marginBottom: '16px' }}>
                                <span style={{ fontSize: '20px' }}>✓</span>
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '15px' }}>Upload succeeded</div>
                                    <div style={{ fontSize: '13px', opacity: 0.9 }}>{selectedFile?.name} was uploaded successfully.</div>
                                </div>
                            </div>
                        )}

                        {/* Uploading progress */}
                        {uploadProgress && !uploadSuccess && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ background: '#1565c0', color: 'white', borderRadius: '8px 8px 0 0', padding: '12px 16px' }}>
                                    <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '6px' }}>Uploading…</div>
                                    <div style={{ background: 'rgba(255,255,255,0.3)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                        <div style={{ height: '100%', background: 'white', borderRadius: '4px', width: '100%', animation: 'rfcSlide 1.5s ease-in-out infinite' }} />
                                    </div>
                                    <style>{`@keyframes rfcSlide { 0%{transform:translateX(-100%)} 100%{transform:translateX(100%)} }`}</style>
                                </div>
                                <div style={{ background: '#e3f2fd', borderRadius: '0 0 8px 8px', padding: '10px 16px', fontSize: '13px', color: '#1565c0' }}>
                                    <strong>File:</strong> {selectedFile?.name} ({selectedFile ? (selectedFile.size / 1024).toFixed(1) : 0} KB)
                                </div>
                            </div>
                        )}

                        {/* File picker — hide while uploading or success */}
                        {!uploadProgress && !uploadSuccess && (
                            <>
                                <input
                                    type="file"
                                    // eslint-disable-next-line jsx-a11y/no-autofocus
                                    autoFocus
                                    onChange={e => { setSelectedFile(e.target.files[0]); setUploadError(""); }}
                                    style={{
                                        width: '100%', padding: '10px', border: '1px dashed #d41042',
                                        borderRadius: '8px', marginBottom: '20px',
                                        fontSize: '14px', cursor: 'pointer', boxSizing: 'border-box'
                                    }}
                                />
                                {selectedFile && (
                                    <p style={{ margin: '-12px 0 16px 0', fontSize: '13px', color: '#555' }}>
                                        Selected: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
                                    </p>
                                )}
                                {uploadError && (
                                    <p style={{ margin: '0 0 14px 0', fontSize: '13px', color: '#c0392b', background: '#fdf0f0', borderRadius: '6px', padding: '8px 12px' }}>
                                        {uploadError}
                                    </p>
                                )}
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button onClick={handleFileUpload} disabled={!selectedFile}
                                        style={{
                                            flex: 1, padding: '12px', backgroundColor: !selectedFile ? '#e0a0b0' : '#d41042',
                                            color: 'white', border: 'none', borderRadius: '8px',
                                            fontSize: '15px', fontWeight: 500, cursor: !selectedFile ? 'not-allowed' : 'pointer'
                                        }}>
                                        Upload
                                    </button>
                                    <button onClick={() => { setAddFileModalOpen(false); setUploadError(""); setUploadSuccess(false); }}
                                        style={{
                                            flex: 1, padding: '12px', backgroundColor: '#f5f5f5',
                                            color: '#555', border: 'none', borderRadius: '8px',
                                            fontSize: '15px', fontWeight: 500, cursor: 'pointer'
                                        }}>
                                        Cancel
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Add Folder Modal */}
            {addFolderModalOpen && (
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', zIndex: 9999
                    }}
                    onClick={() => { setAddFolderModalOpen(false); setUploadError(""); }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="add-folder-modal-title"
                        style={{
                            backgroundColor: 'white', borderRadius: '12px', padding: '32px',
                            maxWidth: '480px', width: '90%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            position: 'relative'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <IconButton onClick={() => { setAddFolderModalOpen(false); setUploadError(""); }}
                            style={{ position: 'absolute', top: '12px', right: '12px', color: '#666' }}>
                            <CloseIcon />
                        </IconButton>
                        <h2 id="add-folder-modal-title" style={{ marginTop: 0, marginBottom: '8px', color: '#333', fontSize: '20px', fontWeight: 600 }}>
                            Add Folder
                        </h2>
                        <p style={{ margin: '0 0 16px 0', color: '#888', fontSize: '13px' }}>
                            Creating inside: <strong style={{ color: '#d41042' }}>{currentPrefix || 'Root'}</strong>
                        </p>
                        <input
                            type="text"
                            placeholder="Folder name"
                            value={newFolderName}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                            onChange={e => { setNewFolderName(e.target.value); setUploadError(""); }}
                            onKeyDown={e => { if (e.key === 'Enter') handleCreateFolder(); if (e.key === 'Escape') { setAddFolderModalOpen(false); setUploadError(""); } }}
                            style={{
                                width: '100%', padding: '12px 14px', border: '1px solid #d0d0d0',
                                borderRadius: '8px', fontSize: '15px', marginBottom: '20px',
                                outline: 'none', boxSizing: 'border-box'
                            }}
                        />
                        {uploadError && (
                            <p style={{ margin: '-12px 0 14px 0', fontSize: '13px', color: '#c0392b', background: '#fdf0f0', borderRadius: '6px', padding: '8px 12px' }}>
                                {uploadError}
                            </p>
                        )}
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={handleCreateFolder} disabled={!newFolderName.trim() || uploadProgress}
                                style={{
                                    flex: 1, padding: '12px',
                                    backgroundColor: !newFolderName.trim() || uploadProgress ? '#e0a0b0' : '#d41042',
                                    color: 'white', border: 'none', borderRadius: '8px',
                                    fontSize: '15px', fontWeight: 500,
                                    cursor: !newFolderName.trim() || uploadProgress ? 'not-allowed' : 'pointer'
                                }}>
                                {uploadProgress ? 'Creating…' : 'Create'}
                            </button>
                            <button onClick={() => { setAddFolderModalOpen(false); setUploadError(""); }}
                                style={{
                                    flex: 1, padding: '12px', backgroundColor: '#f5f5f5',
                                    color: '#555', border: 'none', borderRadius: '8px',
                                    fontSize: '15px', fontWeight: 500, cursor: 'pointer'
                                }}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

}

export default RFCLibrary;